export const GRID_SIZE = 16 // px - the holy unit
export const BYTES_PER_KILOBYTE = 1024

// Paper widths in grid units
export const A8_WIDTH = 22  // 352px
export const A6_WIDTH = 44  // 704px  
export const A4_WIDTH = 88  // 1408px

// Paper heights in grid units
// These define the tiling rectangle for each paper size
export const A8_HEIGHT = 31  // 496px
export const A6_HEIGHT = 62  // 992px
export const A4_HEIGHT = 124 // 1984px
export const A2_WIDTH = A4_WIDTH * 2
export const A2_HEIGHT = A4_HEIGHT * 2
export const A0_WIDTH = A4_WIDTH * 4
export const A0_HEIGHT = A4_HEIGHT * 4
// ANSI sizes (approx in grid units; base on 8.5x11 scaled to 16px grid)
// Snapped to grid units with quarter = 23u for ANSI A (letter)
export const ANSI_QUARTER_WIDTH = 23
export const ANSI_QUARTER_HEIGHT = 30
export const LETTER_WIDTH = 92   // ANSI A width
export const LETTER_HEIGHT = 119 // ANSI A height (rounded)
export const ANSI_C_WIDTH = 184  // 2x letter
export const ANSI_C_HEIGHT = 238 // 2x letter
export const ANSI_E_WIDTH = 368  // 2x ANSI C
export const ANSI_E_HEIGHT = 476 // 2x ANSI C

// Maximum width multiple (relative to A8) allowed when resizing
export const MAX_A8_WIDTH_MULTIPLIER = 32

// Visual specs
export const FONT = '"IBM Plex Mono", "SF Mono", Monaco, "Cascadia Code", "Roboto Mono", Consolas, "Courier New", monospace'
export const FONT_SIZE = 16 // px
export const LINE_HEIGHT = 32 // px - 2 grid units for generous spacing
export const FONT_WEIGHT_NORMAL = 400
export const FONT_WEIGHT_BOLD = 800

// Colors for extended staring without fatigue
export const COLORS = {
  BACKGROUND: '#FDFDFD',    // Monochrome paper white
  TEXT: '#1D1D1D',          // Muted ink for text, connections, handles
  HANDLES: '#1D1D1D',       // Keep handles unified with text/lines
  BASE_GRID: '#E0E0E0',     // Solid base grid (matches prior blended tone)
  PAPER_GRID: '#E0E0E0',    // Paper guides (aligned with base grid tone)
  CMD_BUTTON: '#C50F1F',    // Findable without screaming
  CARET: '#0037DA',         // Complementary blue for text caret
} as const

// Performance constants
export const AUTOSAVE_DEBOUNCE_MS = 400
export const DELETION_RECEIPT_TEXT = '/deleted'
export const LOCAL_CONSOLIDATION_DEBOUNCE_MS = 4000
export const TARGET_FPS = 60
export const SYNC_DEBOUNCE_MS = 4000
export const FETCH_TIMEOUT_MS = 4000
export const SYNC_SNAPSHOT_READ_ATTEMPTS = 2
export const SYNC_UPLOAD_NAME_ATTEMPTS = 2
export const SYNC_CLEANUP_BATCH_LIMIT = 10
export const SYNC_PAYLOAD_FETCH_TIMEOUT_MS = 15000
export const SYNC_SHELF_HYDRATION_ITEM_LIMIT = 2
export const SYNC_SHELF_HYDRATION_BYTE_LIMIT = 512 * 1024
export const SYNC_SHELF_HYDRATION_QUIET_GAP_MS = 1000
export const SYNC_SHELF_HYDRATION_AGE_STEP_MS = 60000
export const SYNC_SHELF_HYDRATION_RETRY_BASE_MS = SYNC_DEBOUNCE_MS
export const SYNC_SHELF_HYDRATION_RETRY_MAX_MS = 60000
export const META_SPACES_ID = '@meta/spaces'

// View + Zoom constants (single source)
// Margin used when fitting paper sizes to the viewport (in grid units)
export const VIEW_FIT_MARGIN_UNITS = 2 // = 32px total per side when GRID_SIZE=16
// Initial view offset in grid units (breathing room from origin on load)
export const VIEW_START_MARGIN_UNITS = 1
export const VIEW_START_MARGIN_DESKTOP_UNITS = 4
export const VIEW_START_MARGIN_TOUCH_UNITS = 2
// Desktop hard zoom-in limit (4x per spec)
export const DESKTOP_MAX_ZOOM = 4
export const MIN_ZOOM = 0.125
export const TOUCH_MAX_ZOOM = 2
export const DESKTOP_WHEEL_ZOOM_SENSITIVITY = 0.0025
export const DESKTOP_WHEEL_ZOOM_LINEAR_STEP = 0.0125
export const TOUCH_ZOOM_SCRUB_LINEAR_STEP = 0.0075

// Interaction tolerances
// Handle hit radius in CSS pixels (32px diameter target for resize/connect)
export const HANDLE_HIT_RADIUS_PX = 16
// Connection line hit tolerance for selection (±16px)
export const CONNECTION_HIT_TOLERANCE_PX = 16
// Touch tap detection thresholds
export const TOUCH_TAP_MAX_DURATION_MS = 400
export const TOUCH_TAP_MOVE_TOLERANCE_PX = 8
export const AREA_SELECT_LONG_PRESS_MS = 500
export const AREA_SELECT_LONG_PRESS_MOVE_TOLERANCE_PX = 8
export const AREA_SELECT_SHIFT_DRAG_THRESHOLD_PX = 4
// Mouse jitter under this distance stays a click, never a drag/pan gesture
export const DRAG_INTENT_THRESHOLD_PX = 4
export const AREA_SELECT_OUTLINE_PX = 2
export const AREA_SELECT_RECT_STROKE_PX = 2
export const AREA_SELECT_RECT_ALPHA = 0.9

// Text block paddings (single source)
// Horizontal padding in grid units (1u = 16px)
export const TEXT_PADDING_X_UNITS = 1
// Top/bottom padding in world pixels for subtle optical alignment
export const TEXT_PADDING_TOP_PX = 8
// Borrow one grid line at top by shifting content down; reflect as bottom padding
export const TEXT_PADDING_BOTTOM_PX = GRID_SIZE // 16px
// CSS baseline compensation to align overlay caret with canvas glyphs
export const CSS_BASELINE_ADJUST_PX = 0
const IOS_UA = typeof navigator !== 'undefined' ? navigator.userAgent : ''
const IS_IOS = /iPad|iPhone|iPod/.test(IOS_UA)
  || (IOS_UA.includes('Mac') && (typeof navigator !== 'undefined') && (navigator.maxTouchPoints || 0) > 1)
export const TEXT_BASELINE_SHIFT_PX = IS_IOS ? -4 : 0

// Trim one grid line from computed block height so the
// selection frame and End+ sit one grid closer to the text bottom.
export const TEXT_BOTTOM_TRIM_PX = TEXT_PADDING_TOP_PX + TEXT_PADDING_BOTTOM_PX
export const MOBILE_EDITOR_DONE_BENTO_HEIGHT_PX = GRID_SIZE * 3
export const MOBILE_EDITOR_DONE_GAP_PX = GRID_SIZE / 2

// Handle visuals
export const HANDLE_HALO_ARM_PX = 4  // matches crosshair arm length
export const HANDLE_HALO_PADDING_PX = 2 // extra padding around the crosshair for halo

// Selection frame styling (single source)
export const SELECT_MIN_LINE_PX = 2 // minimum screen px line width
export const SELECT_HIGH_LINE_PX = 4 // at high zoom
export const SELECT_DASH_BASE_ON_PX = 2 // base dash on length in screen px
export const SELECT_DASH_BASE_OFF_PX = 4 // base dash off length in screen px (1:2 ratio)
// Hard bottom for dash pitch in screen px (half of A6 pitch at 1x = GRID/4)
export const SELECT_DASH_MIN_PITCH_PX = GRID_SIZE / 4

// Selection fill styling (replaces dotted frame)
export const SELECT_FILL_ALPHA = 1
export const SELECT_FILL_COLOR = '#E7E7E7'
export const EDITING_FILL_COLOR = COLORS.BACKGROUND
export const SELECT_CORNER_RADIUS_PX = GRID_SIZE // 16px rounding for selection/edit wash
export const SELECT_INSET_STROKE = 'rgba(17, 24, 39, 0.08)'
export const EDITING_SHADOW_COLOR = 'rgba(0, 0, 0, 0.18)'
export const EDITING_SHADOW_BLUR_PX = GRID_SIZE * 2
export const EDITING_SHADOW_OFFSET_Y_PX = GRID_SIZE / 2
export const CODE_INLINE_FILL = '#6B7280'
export const CODE_INLINE_FILL_ALPHA = 0.12
export const CODE_INLINE_STROKE = '#9CA3AF'
export const CODE_HIGHLIGHT_INSET_TOP_PX = -2
export const CODE_HIGHLIGHT_INSET_BOTTOM_PX = 14
export const SEARCH_MARK_STROKE_PX = 1.5 // css px, held constant across zoom

// Paper size labels (UI overlay on canvas)
export const LABEL_FONT_FAMILY = 'Helvetica, Arial, sans-serif'
export const LABEL_FONT_SIZE = 176 // px
export const LABEL_ALPHA = 0.2

// Paper/grid visuals (single source)
export const GRID_DOT_RADIUS_CLAMP = { min: 0.4, mid: 1.2, max: 2.8 } // base dots (screen px at 1x)
export const PAPER_A8_STROKE_CLAMP = { min: 0.5, mid: 1, max: 2 } // screen px
export const PAPER_A4_STROKE_CLAMP = { min: 1, mid: 2, max: 4 }   // screen px
export const PAPER_HIDE_BELOW_SCALE = 0.25
export const PAPER_LABEL_HIDE_BELOW_SCALE = MIN_ZOOM + 0.01
export const PAPER_LABEL_COLOR = COLORS.BASE_GRID

// Paper types (grid units; quarter is the snap column, eighth is reserved for future)
export const PAPER_TYPES = {
  ISO_V: {
    id: 'ISO-V',
    label: 'ISO-vertical',
    nominal: { w: 80, h: 112 },
    quarter: { w: 20, h: 28 },
    eighth: { w: 10, h: 14 },
  },
  ISO_H: {
    id: 'ISO-H',
    label: 'ISO-horizontal',
    nominal: { w: 112, h: 80 },
    quarter: { w: 28, h: 20 },
    eighth: { w: 14, h: 10 },
  },
  ANSI_V: {
    id: 'ANSI-V',
    label: 'ANSI-vertical',
    nominal: { w: 80, h: 104 },
    quarter: { w: 20, h: 26 },
    eighth: { w: 10, h: 13 },
  },
  ANSI_H: {
    id: 'ANSI-H',
    label: 'ANSI-horizontal',
    nominal: { w: 104, h: 80 },
    quarter: { w: 26, h: 20 },
    eighth: { w: 13, h: 10 },
  },
} as const

export const DEFAULT_PAPER_TYPE_ID = 'ISO_V'
export const LILODATA_PREFIX = '/lilodata'
export const LILODATA_BLOCK_ID = 'lilodata'

// Date/time snippets (editor expansion; longest trigger matched first)
export const SNIPPET_TRIGGERS = ['!DT', '!D', '!T'] as const
export const SNIPPET_BOUNDARY_CHARS = ' \t\n.,;:!?()[]{}"\''

export const PAPER_SPECS = {
  ISO: {
    name: 'ISO',
    small: { w: A8_WIDTH, h: A8_HEIGHT },
    medium: { w: A6_WIDTH, h: A6_HEIGHT },
    large: { w: A4_WIDTH, h: A4_HEIGHT },
  },
  // NOTE: ISO_H (horizontal) intentionally omitted until text layout/snapping
  // can be parameterized for horizontal paper columns.
  ANSI: {
    name: 'ANSI',
    small: { w: ANSI_QUARTER_WIDTH, h: ANSI_QUARTER_HEIGHT }, // quarter of ANSI A
    medium: { w: LETTER_WIDTH, h: LETTER_HEIGHT },            // ANSI A (Letter)
    large: { w: ANSI_C_WIDTH, h: ANSI_C_HEIGHT },              // ANSI C
  },
  SQUARE: {
    name: 'SQUARE',
    small: { w: 25, h: 25 },
    medium: { w: 50, h: 50 },
    large: { w: 100, h: 100 },
  },
  NONE: {
    name: 'NONE',
    small: { w: 0, h: 0 },
    medium: { w: 0, h: 0 },
    large: { w: 0, h: 0 },
  }
} as const
